import { type PropsWithChildren, useRef } from 'react'
import { type DropTargetMonitor, useDrag, useDrop } from 'react-dnd'
import { type XYCoord } from 'dnd-core'

interface Props {
	id: string | number;
	index: number;
	className: string;
	moveCard: (dragIndex: number, hoverIndex: number) => void;
}

interface DragItem {
	id: string;
	index: number;
	type: string;
}

export function DraggableCard({id, index, className, moveCard, children}: PropsWithChildren<Props>) {
	const ref = useRef<HTMLDivElement>(null)
	const [{handlerId}, drop] = useDrop({
		accept: 'DraggableCard',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			}
		},
		hover(item: DragItem, monitor: DropTargetMonitor) {
			if (!ref.current) {
				return
			}
			const dragIndex = item.index
			const hoverIndex = index

			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return
			}

			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect()

			// Get vertical middle
			const hoverMiddleY =
				(hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

			// Determine mouse position
			const clientOffset = monitor.getClientOffset()

			// Get pixels to the top
			const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%

			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return
			}

			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return
			}

			// Time to actually perform the action
			moveCard(dragIndex, hoverIndex)

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex
		},
	})

	const [{isDragging}, drag] = useDrag({
		type: 'DraggableCard',
		item: () => {
			return {id, index}
		},
		collect: (monitor: any) => ({
			isDragging: monitor.isDragging(),
		}),
	})

	const opacity = isDragging ? 0 : 1
	drag(drop(ref))
	return (
		<div ref={ref} className={className} style={{opacity}} data-handler-id={handlerId}>
			{children}
		</div>
	)
}
